import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Form from 'components/Form';
import TextField from 'components/TextField';
import Checkbox from 'components/Checkbox';
import KeyValueArrayField, {
  KeyValuePair,
} from 'components/KeyValueArrayField';

import { useErrorHandler } from 'lib';

import {
  KiwiSettings_settings$data,
  KiwiSettings_settings$key,
} from '__generated__/KiwiSettings_settings.graphql';
import {
  FlightProvider,
  KiwiSettingsMutation,
} from '__generated__/KiwiSettingsMutation.graphql';

type FormFields = {
  baseUri: string;
  apiKey: string;
  testMode: boolean;
  searchParams: KeyValuePair[];
};

type Props = {
  settings: KiwiSettings_settings$key;
};

function updateFlightProviderSettings(
  current: KiwiSettings_settings$data['flightsProviderSettings'],
  values: FormFields
) {
  let found = false;
  const update = {
    flightsProvider: 'KIWI' as FlightProvider,
    ...values,
  };

  const newSettings =
    current?.map(provider => {
      if (provider?.flightsProvider === 'KIWI') {
        found = true;
        return update;
      } else {
        return provider;
      }
    }) ?? [];

  if (!found) {
    newSettings.push(update);
  }
  return newSettings;
}

const KiwiSettings: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const { flightsProviderSettings } = useFragment(
    graphql`
      fragment KiwiSettings_settings on Settings {
        flightsProviderSettings {
          flightsProvider
          baseUri
          testMode
          apiKey

          searchParams {
            key
            value
          }
        }
      }
    `,
    props.settings
  );

  const [update, updating] = useMutation<KiwiSettingsMutation>(graphql`
    mutation KiwiSettingsMutation($input: UpdateSettingsInput!) {
      updateSettings(input: $input) {
        ...KiwiSettings_settings
      }
    }
  `);

  const settings = flightsProviderSettings?.find(
    item => item?.flightsProvider === 'KIWI'
  );

  const initial: FormFields = {
    baseUri: settings?.baseUri ?? '',
    apiKey: settings?.apiKey ?? '',
    testMode: settings?.testMode ?? false,
    searchParams:
      settings?.searchParams?.map(item => ({
        ...item,
        value: item?.value ?? '',
      })) ?? [],
  };

  const handleSubmit = useCallback(
    (values: FormFields) => {
      update({
        variables: {
          input: {
            flightsProviderSettings: updateFlightProviderSettings(
              flightsProviderSettings,
              values
            ),
          },
        },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, flightsProviderSettings, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={4}>
            <TextField fullWidth name="baseUri" label="API Adresas" />
            <TextField fullWidth name="apiKey" label="API Key" />

            <FormControlLabel
              control={<Checkbox name="testMode" />}
              label="Testinis režimas"
            />

            <KeyValueArrayField
              name="searchParams"
              label="Paieškos parametrai"
            />
          </Stack>
        </Paper>
      </Form>
    </Formik>
  );
};

export default KiwiSettings;
